<template>
  <div class="dropdown-root" ref="root">
    <button
      type="button"
      class="dropdown-trigger"
      :class="{ 'dropdown-trigger--active': showMenu }"
      aria-label="Row actions"
      @click.stop="toggleMenu"
    >
      <span class="mdi mdi-dots-vertical"></span>
    </button>
    <transition name="menu-fade">
      <div
        v-if="showMenu"
        ref="menu"
        class="menu"
        :class="{ 'menu--up': openUpward }"
        :style="menuStyle"
        @click.stop
      >
        <div
          v-for="item in items"
          :key="item"
          class="menu-item"
          :class="itemClass(item)"
          @click.stop="handleItems(item, rowData)"
        >
          <span
            v-if="itemIcon(item)"
            :class="['menu-item-icon mdi', itemIcon(item)]"
          ></span>
          <span class="menu-item-label">{{ item }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const ACTION_ICONS = {
  Edit: "mdi-pencil-outline",
  Cancel: "mdi-close-circle-outline",
  "Cancel Study": "mdi-file-remove-outline",
  Discount: "mdi-tag-outline",
  "Add Attachment": "mdi-paperclip",
  "View Attachment": "mdi-eye-outline",
  Verified: "mdi-check-circle-outline",
  Delete: "mdi-delete-outline",
  View: "mdi-eye-outline",
  Print: "mdi-printer-outline"
};

const DANGER_ACTIONS = new Set(["Cancel", "Cancel Study", "Delete"]);

export default {
  props: {
    items: {
      type: Array
    },
    handleItemClick: {
      type: Function,
      default: () => {}
    },
    rowData: {
      type: Object
    }
  },
  data() {
    return {
      showMenu: false,
      menuStyle: {},
      openUpward: false
    };
  },
  methods: {
    itemIcon(item) {
      return ACTION_ICONS[item] || null;
    },
    itemClass(item) {
      return {
        "menu-item--danger": DANGER_ACTIONS.has(item)
      };
    },
    handleItems(item, rowData) {
      this.handleItemClick(item, rowData);
      this.showMenu = false;
    },
    positionMenu() {
      const trigger = this.$refs.root;
      const menu = this.$refs.menu;
      if (!trigger || !menu) {
        return;
      }

      const rect = trigger.getBoundingClientRect();
      const menuWidth = 220;
      const gap = 6;
      const viewportPadding = 8;

      let left = rect.right - menuWidth;
      if (left < viewportPadding) {
        left = viewportPadding;
      }
      if (left + menuWidth > window.innerWidth - viewportPadding) {
        left = window.innerWidth - menuWidth - viewportPadding;
      }

      const menuHeight = menu.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
      const spaceAbove = rect.top - viewportPadding;

      let top;
      let openUpward = false;
      let maxHeight;

      if (menuHeight + gap <= spaceBelow) {
        top = rect.bottom + gap;
      } else if (menuHeight + gap <= spaceAbove) {
        top = rect.top - menuHeight - gap;
        openUpward = true;
      } else if (spaceBelow >= spaceAbove) {
        top = rect.bottom + gap;
        maxHeight = Math.max(spaceBelow - gap, 120);
      } else {
        top = viewportPadding;
        openUpward = true;
        maxHeight = Math.max(spaceAbove - gap, 120);
      }

      this.openUpward = openUpward;
      this.menuStyle = {
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        minWidth: `${menuWidth}px`,
        zIndex: 10000,
        ...(maxHeight ? { maxHeight: `${maxHeight}px`, overflowY: "auto" } : {})
      };
    },
    toggleMenu() {
      if (this.showMenu) {
        this.showMenu = false;
        return;
      }

      const rect = this.$refs.root.getBoundingClientRect();
      const menuWidth = 220;

      let left = rect.right - menuWidth;
      if (left < 8) {
        left = 8;
      }
      if (left + menuWidth > window.innerWidth - 8) {
        left = window.innerWidth - menuWidth - 8;
      }

      this.menuStyle = {
        position: "fixed",
        top: `${rect.bottom + 6}px`,
        left: `${left}px`,
        minWidth: `${menuWidth}px`,
        zIndex: 10000,
        visibility: "hidden"
      };
      this.showMenu = true;

      this.$nextTick(() => {
        this.positionMenu();
        this.menuStyle = {
          ...this.menuStyle,
          visibility: "visible"
        };
      });
    },
    close(e) {
      if (!this.$refs.root?.contains(e.target)) {
        this.showMenu = false;
      }
    },
    onViewportChange() {
      if (this.showMenu) {
        this.positionMenu();
      }
    }
  },
  mounted() {
    document.addEventListener("click", this.close);
    window.addEventListener("resize", this.onViewportChange);
    window.addEventListener("scroll", this.onViewportChange, true);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.close);
    window.removeEventListener("resize", this.onViewportChange);
    window.removeEventListener("scroll", this.onViewportChange, true);
  }
};
</script>

<style scoped>
.dropdown-root {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.dropdown-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-left: 8px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--muted-soft);
  cursor: pointer;
  transition: background-color var(--dur) var(--ease),
    color var(--dur) var(--ease), box-shadow var(--dur) var(--ease);
}

.dropdown-trigger .mdi {
  font-size: 1.25rem;
  line-height: 1;
}

.dropdown-trigger:hover,
.dropdown-trigger--active {
  background: var(--surface-2);
  color: var(--accent-600);
  box-shadow: inset 0 0 0 1px var(--line);
}

.menu {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md), 0 12px 32px rgba(26, 19, 54, 0.12);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 38px;
  padding: 8px 12px;
  border-radius: calc(var(--radius-md) - 2px);
  font-size: var(--fs-sm);
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0.01em;
  text-transform: none;
  white-space: nowrap;
  color: var(--text);
  cursor: pointer;
  transition: background-color var(--dur) var(--ease),
    color var(--dur) var(--ease);
}

.menu-item-icon {
  flex-shrink: 0;
  width: 18px;
  font-size: 1.05rem;
  color: var(--muted);
  transition: color var(--dur) var(--ease);
}

.menu-item-label {
  flex: 1;
}

.menu-item:hover {
  background: rgba(109, 84, 199, 0.08);
  color: var(--ink-900);
}

.menu-item:hover .menu-item-icon {
  color: var(--accent-600);
}

.menu-item--danger {
  color: #c62828;
}

.menu-item--danger .menu-item-icon {
  color: #e57373;
}

.menu-item--danger:hover {
  background: rgba(198, 40, 40, 0.08);
  color: #b71c1c;
}

.menu-item--danger:hover .menu-item-icon {
  color: #c62828;
}

.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.14s var(--ease), transform 0.14s var(--ease);
}

.menu-fade-enter,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

.menu-fade-enter.menu--up,
.menu-fade-leave-to.menu--up {
  transform: translateY(4px) scale(0.98);
}
</style>
